import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, finalize, first } from 'rxjs/operators';
import { BaseComponent } from '../../shared/base-components/base.component';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { TitleRegistrationService } from '../../titleregistration/services/titleregistration.service';
import { CustomerMvdNowStatus } from '../models/customer-mvd-now-status.model';
import { CustomerManageService } from '../shared/customer-manage.service';
import { MVDNowViewService } from './mvdnow-view.service';
import { MVDNowLinkCustomerContext, MVDNowLinkCustomerModalComponent } from './mvdnow-link-customer-modal.component';
import { ModalSize } from '../../shared/services/modal/mvd-modal.enum';
import { MobileIdentificationNumber } from '../../avocet/models/mobile-identification-number.model';
import { MobileIdentificationStatusType } from '../../avocet/models/mobile-identification-status-type.enum';
import { MobileIdentificationCustomerAuthorization } from '../../avocet/models/mobile-identification-customer-authorization.model';
import { MobileIdentificationProvisioningBundle } from '../../avocet/models/mobile-identification-provisioning-bundle.model';
import { MobileIdentificationProvisioningBundleStatusType } from '../../avocet/models/mobile-identification-provisioning-bundle-status-type.enum';
import { MVDNowChangeEmailModalComponent } from './mvdnow-change-email-modal.component';
import { MobileIdentificationCredentialActionRequestModalComponent, MobileIdentificationCredentialActionRequestModalContext } from './mobile-identification-credential-action-request-modal.component';
import { MobileIdentificationCredentialActionRequest } from './models/mobile-identification-credential-action-request.model';
import { MobileIdentificationCredentialActionType } from '../../MobileIdentification/models/enums/mobile-identification-credential-action-type.enum';
import { IMobileIdentificationCustomerAuthorizationStatusType, MobileIdentificationCustomerAuthorizationStatusType } from '@max/driver/models/enums/mobile-identification-customer-authorization-status-type.enum';
import { MobileIdentificationCredentialType } from '@max/MobileIdentification/models/enums/mobile-identification-credential-type.enum';

@Component({
    selector: 'mvdnow-view-detail',
    templateUrl: './mvdnow-view-detail.html'
})
export class MVDNowViewDetailComponent extends BaseComponent implements OnDestroy {
    public vehicleData;
    public mvdNowStatus: CustomerMvdNowStatus;
    private customerNumber: number;
    private viewServiceSubscription: Subscription;

    public loading = false;
    public showMobileId = false;
    public isCustomerMobileIdOptedOut = false;
    public currentMobileIds: MobileIdentificationNumber[];
    public mobileIdentificationCustomerAuthorizationStatusTypes: IMobileIdentificationCustomerAuthorizationStatusType[];
    public mobileIdentificationCustomerAuthorizationStatusType = new MobileIdentificationCustomerAuthorizationStatusType();
    public mobileProvisioningBundles: MobileIdentificationProvisioningBundle[];
    public mobileIdAuthorization: MobileIdentificationCustomerAuthorization;
    public mobileIdentificationStatusTypes = MobileIdentificationStatusType;
    public mobileIdentificationProvisioningBundleStatusType = MobileIdentificationProvisioningBundleStatusType;
    public mobileIdentificationCredentialTypes = new MobileIdentificationCredentialType();
    public mobileIdHistory: MobileIdentificationNumber[];
    public customerAuthorizationHistory: MobileIdentificationCustomerAuthorization[];
    public idemiaMId: any;
    public mfaResetInProgress: boolean;
    public bundleUnlinkInProgress: boolean;
    public hasActiveV4IdemiaMId = false;
    private mobileIdentificationCredentialActionType = new MobileIdentificationCredentialActionType();

    constructor(
        public modalService: MvdModalService,
        private titleRegistrationService: TitleRegistrationService,
        private customerService: CustomerManageService,
        private viewService: MVDNowViewService,
        private router: Router
    ) {
        super();
    }

    public ngOnInit() {
        this.loading = true;

        // Get customer authorization status types
        this.mobileIdentificationCustomerAuthorizationStatusTypes = this.mobileIdentificationCustomerAuthorizationStatusType.GetAll();

        this.viewServiceSubscription = this.viewService.customerNumberSubject.subscribe(
            async customerNumber => {
                try {
                    if (customerNumber) {
                        this.customerNumber = customerNumber;
                        this.vehicleData = await this.titleRegistrationService.getCustomerVehiclesWithActiveRegistration(customerNumber).pipe(first()).toPromise();
                    }
                } catch (err) {
                    this.handleValidationResult(err);
                }

                try {
                    if (customerNumber) {
                        this.mvdNowStatus = (await this.customerService.getMvdNowStatus(customerNumber).pipe(first()).toPromise());
                    }
                } catch (err) {
                    this.handleValidationResult(err);
                }

                try {
                    if (customerNumber) {
                        this.isCustomerMobileIdOptedOut = (await this.customerService.isCustomerMobileIdOptedOut(customerNumber).pipe(first()).toPromise());
                    }
                } catch (err) {
                    this.handleValidationResult(err);
                }

                // Avocet Mobile ID
                try {
                    if (customerNumber) {
                        this.mobileIdAuthorization = (await this.customerService.getMobileIdentificationCustomerAuthorization(customerNumber).pipe(first(), finalize(() => {
                            this.loading = false;
                        })).toPromise());

                        this.currentMobileIds = await this.getMobileIds();
                        const mobileProvisioningBundleSets = this.currentMobileIds.map(m => {
                            const mobileProvisioningBundleSet = m.mobileIdentificationProvisioningBundle;
                            for (const mobileProvisioningBundle of mobileProvisioningBundleSet) {
                                mobileProvisioningBundle.mobileIdentificationCredentialTypeDisplayValue = m.mobileIdentificationCredentialTypeDisplayValue;
                                mobileProvisioningBundle.mobileIdentificationCredentialTypeId = m.mobileIdentificationCredentialTypeId;
                            }

                            return m.mobileIdentificationProvisioningBundle;
                        });
                        let mobileProvisioningBundles = [];
                        if (mobileProvisioningBundleSets && mobileProvisioningBundleSets.length) {
                            mobileProvisioningBundles = mobileProvisioningBundleSets.reduce((mobileProvisioningBundles, mobileProvisioningBundleSet) => mobileProvisioningBundles.concat(mobileProvisioningBundleSet));
                            this.mobileProvisioningBundles = mobileProvisioningBundles;
                        }

                        this.showMobileId = (await this.customerService.getMobileIdUIEnabled().pipe(first()).toPromise());
                        this.mobileIdHistory = (await this.customerService.getMobileIdentificationNumberHistory(customerNumber).pipe(first()).toPromise());
                        this.mobileIdHistory = this.mobileIdHistory ? this.mobileIdHistory.sort((a, b) => {
                            var requestedDateDiff = new Date(b.requestedDate).getTime() - new Date(a.requestedDate).getTime();
                            var updatedDateDiff = new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime();
                            return requestedDateDiff !== 0 ? requestedDateDiff : updatedDateDiff;
                        }) : null;

                        this.customerAuthorizationHistory = (await this.customerService.getMobileIdentificationCustomerAuthorizationHistory(customerNumber).pipe(first()).toPromise());

                        this.hasActiveV4IdemiaMId = this.currentMobileIds.some(p => p.mobileIdentificationCredentialTypeId == this.mobileIdentificationCredentialTypes?.IdemiaMDL.id && p.mobileIdentificationStatusTypeId == MobileIdentificationStatusType.Active);
                    }
                } catch (err) {
                    this.handleValidationResult(err);
                    this.loading = false;
                }

                // Idemia MId
                try {
                    if (customerNumber) {
                        var result = (await this.customerService.getLatestMId(customerNumber).pipe(first()).toPromise());
                        this.idemiaMId = result.id ? result : null;
                    }
                } catch (err) {
                    this.handleValidationResult(err);
                }
            }
        );
    }

    public ngOnDestroy() {
        if (this.viewServiceSubscription) {
            this.viewServiceSubscription.unsubscribe();
            this.viewServiceSubscription = null;
        }
        super.ngOnDestroy();
    }

    public emitDocumentSelected(): void {
        this.viewService.documentSelected();
    }

    private emitMessageSelected(): void {
        this.viewService.messageSelected();
    }

    public emitTitleSelected(vehicleId: number): void {
        this.viewService.titleSelected(vehicleId);
    }

    public async toggleMvdNowAccess() {
        try {
            await this.customerService.setMvdNowAccess(this.viewService.customerNumberSubject.value, !this.mvdNowStatus.userAccessToMvdNowEnabled).pipe(first()).toPromise();
            this.mvdNowStatus.userAccessToMvdNowEnabled = !this.mvdNowStatus.userAccessToMvdNowEnabled;
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async resetPhoneAuthenticationMethod() {
        if (this.mfaResetInProgress) {
            return;
        }
        const message = 'This customer\'s verification phone number will be removed from their AZ MVD Now login. They must enter a new phone number after the next login.';
        const title = 'Reset AZ MVD Now Login Verification Phone Number';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Continue').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(async () => {
                try {
                    this.mfaResetInProgress = true;
                    await this.customerService.resetPhoneAuthenticationMethod(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();
                    const message = 'This customer\'s phone number has been reset. They must enter a new phone number after the next login.';
                    const title = 'Reset AZ MVD Now Login Verification Phone Number';
                    this.modalService.alert().title(title).body(message).okBtn('Close').open();
                } catch (err) {
                    this.handleValidationResult(err);
                } finally {
                    this.mfaResetInProgress = false;
                }

            }, () => { });
    }

    public async changeEmail() {
        const data = new MVDNowLinkCustomerContext(this.customerNumber);
        this.modalService.open(MVDNowChangeEmailModalComponent, data, ModalSize.Medium).afterClosed()
            .subscribe(
                async (result) => {
                    if (result) {
                        this.mvdNowStatus.username = result;
                    }
                }
            );
    }

    public async unlinkFromMvdNow() {
        const message = 'All Customer and Associated Accounts will be unlinked from AZMVDNow user.  ' +
            'The Customer will need to re-link their Customer Record on AZMVDNow as if they were a 1st time visitor.';
        const title = 'Unlink Customer and Associated Accounts from AZMVDNow user';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Unlink').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.confirmUnlinkFromMvdNow(), () => { });
    }

    private async confirmUnlinkFromMvdNow() {
        try {
            await this.customerService.unlinkFromMvdNow(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();
            this.mvdNowStatus.userAccessToMvdNowEnabled = null;
            this.mvdNowStatus.userExistsInMvdNow = false;

            const message = 'All Customer and Associated Accounts have been unlinked from AZMVDNow user.';
            const title = 'Unlink Customer and Associated Accounts from AZMVDNow user';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async deactivateEnhancedAuthentication() {
        const message = 'Customer\'s AZMVDNow account will have it\'s Enhanced Authentication deactivated.  ' +
            'Continue?';
        const title = 'Deactivate Enhanced Authentication';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Deactivate').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.confirmDeactivateEnhancedAuthentication(), () => { });
    }

    private async confirmDeactivateEnhancedAuthentication() {
        try {
            await this.customerService.deactivateEnhancedAuthentication(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();
            this.mvdNowStatus.enhancedAuthenticationActive = false;

            const message = 'The customer\'s AZMVDNow account\'s Enhanced Authentication has been deactivated.';
            const title = 'Deactivate Enhanced Authentication';
            this.modalService.alert().title(title).body(message)
                .okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async revokeEIdCredentials() {
        const message = 'Customer\'s eID identification account will have it\'s credentials revoked.  ' +
            'Continue?';
        const title = 'Revoke eID Identification Credentials';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Revoke').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.confirmRevokeEIdCredentials(), () => { });
    }

    private async confirmRevokeEIdCredentials() {
        try {
            await this.customerService.revokeEIdCredentials(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();

            this.mvdNowStatus.userHasValidEIdCredential = false;
            this.mvdNowStatus.userHasHistoricalEIdCredential = true;

            const message = 'The customer\'s AZMVDNow account\'s eID Identification has been deactivated.';
            const title = 'Deactivate Enhanced Identification';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async reinstateEIdCredentials() {
        const message = 'Customer\'s eID identification account will have it\'s credentials reinstated.  ' +
            'Continue?';
        const title = 'Reinstate eID Identification Credentials';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Reinstate').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.confirmReinstateEIdCredentials(), () => { });
    }

    private async confirmReinstateEIdCredentials() {
        try {
            await this.customerService.reinstateEIdCredentials(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();

            this.mvdNowStatus.userHasValidEIdCredential = true;
            this.mvdNowStatus.userHasHistoricalEIdCredential = false;

            const message = 'The customer\'s AZMVDNow account\'s eID identification has been reinstated.';
            const title = 'Reinstate Enhanced Identification';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async revokeMIdCredentials() {
        const message = 'Customer\'s MobileID identification account will have it\'s credentials revoked.  ' +
            'Continue?';
        const title = 'Revoke mID Identification Credentials';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Revoke').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.confirmRevokeMIdCredentials(), () => { });
    }

    private async confirmRevokeMIdCredentials() {
        try {
            var result = await this.customerService.revokeMIdCredentials(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();
            this.idemiaMId = result.id ? result : null;

            this.mvdNowStatus.userHasValidMIdCredential = false;
            this.mvdNowStatus.userHasHistoricalMIdCredential = true;

            const message = 'The customer\'s AZMVDNow account\'s MobileID Identification has been deactivated.';
            const title = 'Deactivate MobileID Identification';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async reinstateMIdCredentials() {
        const message = 'Customer\'s MobileID identification account will have it\'s credentials reinstated.  ' +
            'Continue?';
        const title = 'Reinstate MobileID Identification Credentials';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Reinstate').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.confirmReinstateMIdCredentials(), () => { });
    }

    private async confirmReinstateMIdCredentials() {
        try {
            var result = await this.customerService.reinstateMIdCredentials(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();
            this.idemiaMId = result.id ? result : null;

            this.mvdNowStatus.userHasValidMIdCredential = true;
            this.mvdNowStatus.userHasHistoricalMIdCredential = false;

            const message = 'The customer\'s AZMVDNow account\'s MobileID identification has been reinstated.';
            const title = 'Reinstate MobileID Identification';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async updateMIdCredentials() {
        const message = 'Customer\'s MobileID identification account will have it\'s credentials updated.  ' +
            'Continue?';
        const title = 'Update MobileId Identification Credentials';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Update').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.confirmUpdateMIdCredentials(), () => { });
    }

    private async confirmUpdateMIdCredentials() {
        try {
            var result = await this.customerService.updateMIdCredentials(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();
            this.idemiaMId = result.id ? result : null;

            this.mvdNowStatus.userHasValidMIdCredential = true;
            this.mvdNowStatus.userHasHistoricalMIdCredential = false;

            const message = 'The customer\'s AZMVDNow account\'s MobileID identification has been updated.';
            const title = 'Update MobileID Identification';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async resetMVDNowAccountVerification() {
        const message = 'Customer\'s AZ MVD Now account will have its Account Verification settings reset.  ' +
            'Continue?';
        const title = 'Reset Account Verification';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Reset').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.confirmResetAccountVerification(), () => { });
    }

    private async confirmResetAccountVerification() {
        try {
            await this.customerService.resetMVDNowAccountVerification(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();

            this.mvdNowStatus.accountVerificationEnabled = false;

            const message = 'The customer\'s AZ MVD Now account\'s verification settings have been reset.';
            const title = 'Reset Account Verification';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async prequalifyMobileIdentification() {
        const message = 'This will prequalify the customer for future mobile identification use and will override any existing restrictions. ' +
            'If the customer has a mobile identification request in Extended Review, please update that request directly.';
        const title = 'Prequalify Mobile Identification Credentials';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Continue').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.confirmPrequalifyMobileIdentification(), () => { });
    }

    private async confirmPrequalifyMobileIdentification() {
        try {
            this.mobileIdAuthorization = await this.customerService.prequalifyMobileIdentification(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();

            const message = 'The customer\'s Mobile Identification accounts have been prequalified.';
            const title = 'Prequalify Mobile Identification Credentials';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async prequalifyMobileIdentificationRequest(mobileIdentificationNumberId: number) {
        const message = 'This will prequalify the customer for future mobile identification use and will override any existing restrictions.';
        const title = 'Prequalify Mobile Identification Request';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Continue').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.confirmPrequalifyMobileIdentificationRequest(mobileIdentificationNumberId), () => { });
    }

    private async confirmPrequalifyMobileIdentificationRequest(mobileIdentificationNumberId: number) {
        try {
            this.mobileIdAuthorization = await this.customerService.prequalifyMobileIdentificationRequest(mobileIdentificationNumberId, this.viewService.customerNumberSubject.value).pipe(first()).toPromise();

            const message = 'The customer\'s Mobile Identification request has been prequalified.';
            const title = 'Prequalify Mobile Identification Request';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async restrictMobileIdentification() {
        this.modalService.open(MobileIdentificationCredentialActionRequestModalComponent, ModalSize.Small)
            .afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => {
                this.confirmRestrictMobileIdentification(result);
            });
    }

    private async confirmRestrictMobileIdentification(reasonTypeId: number) {
        try {
            this.mobileIdAuthorization = await this.customerService.restrictMobileIdentification(this.viewService.customerNumberSubject.value, reasonTypeId).pipe(first()).toPromise();

            const message = 'The customer\'s Mobile Identification accounts have been restricted.';
            const title = 'Restrict Mobile Identification Credentials';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async unrestrictMobileIdentification() {
        const message = 'The customer\'s Mobile Identification accounts will be unrestricted. ' +
            'Continue?';
        const title = 'Unrestrict Mobile Identification Credentials';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Unrestrict').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.confirmUnrestrictMobileIdentification(), () => { });
    }

    private async confirmUnrestrictMobileIdentification() {
        try {
            this.mobileIdAuthorization = await this.customerService.unrestrictMobileIdentification(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();

            const message = 'The customer\'s Mobile Identification accounts have been unrestricted.';
            const title = 'Unrestrict Mobile Identification Credentials';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async confirmDenyMobileIdentification(mobileIdentificationCredentialTypeId: number) {
        const message = 'This will deny the customer\'s mobile identification request in Extended Review.';
        const title = 'Deny Mobile Identification Request';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Deny').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.denyMobileIdentification(mobileIdentificationCredentialTypeId), () => { });
    }

    private async denyMobileIdentification(mobileIdentificationCredentialTypeId: number) {
        try {
            await this.customerService.denyMobileIdentification(this.viewService.customerNumberSubject.value, mobileIdentificationCredentialTypeId).pipe(first()).toPromise();
            this.currentMobileIds = await this.getMobileIds();
            await this.getMobileIdHistory();

            const message = 'The customer\'s Mobile Identification request has been denied.';
            const title = 'Deny Mobile Identification Request';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async confirmUnlinkMobileProvisioningBundleId(provisioningBundleId: number) {

        const data: MobileIdentificationCredentialActionRequestModalContext = {
            actionType: this.mobileIdentificationCredentialActionType.Unlink.id
        };

        this.modalService.open(MobileIdentificationCredentialActionRequestModalComponent, data, ModalSize.Small)
            .afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => {
                this.unlinkMobileProvisioningBundleId(provisioningBundleId, result);
            });
    }

    private async unlinkMobileProvisioningBundleId(provisioningBundleId: number, reasonTypeId: number) {
        this.bundleUnlinkInProgress = true;
        try {
            let unlinkedBundle = await this.customerService.unlinkMobileProvisioningBundle(provisioningBundleId, reasonTypeId).pipe(first()).toPromise();
            let index = this.mobileProvisioningBundles.findIndex(x => x.provisioningBundleIdentifier == unlinkedBundle.provisioningBundleIdentifier);
            if (index >= 0) {
                this.mobileProvisioningBundles[index].mobileIdentificationProvisioningBundleStatusTypeDisplayValue = unlinkedBundle.mobileIdentificationProvisioningBundleStatusTypeDisplayValue;
                this.mobileProvisioningBundles[index].mobileIdentificationProvisioningBundleStatusTypeId = unlinkedBundle.mobileIdentificationProvisioningBundleStatusTypeId;
            }
            const message = 'The customer\'s Mobile Identification Device has been unlinked.';
            const title = 'Unlink Mobile Identification Device';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();

        } catch (err) {
            this.handleValidationResult(err);
            window.scrollTo({ top: 0 });
        } finally {
            this.bundleUnlinkInProgress = false;
        }
    }

    public async confirmPauseMobileIdentification(mobileIdNumber: number) {
        const actionType = this.mobileIdentificationCredentialActionType.Suspend.id;
        this.openMobileIdentificationCredentialActionRequestModal(actionType, mobileIdNumber);
    }

    private async pauseMobileIdentification(mobileIdentificationCredentialActionRequest: MobileIdentificationCredentialActionRequest) {
        try {
            await this.customerService.pauseMobileIdentification(mobileIdentificationCredentialActionRequest).pipe(first()).toPromise();
            this.currentMobileIds = await this.getMobileIds();
            await this.getMobileIdHistory();

            const message = 'The customer\'s Mobile Identification has been paused.';
            const title = 'Pause Mobile Identification';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public async confirmResumeMobileIdentification(mobileIdNumber: number) {
        const actionType = this.mobileIdentificationCredentialActionType.Resume.id;
        this.openMobileIdentificationCredentialActionRequestModal(actionType, mobileIdNumber);
    }

    private openMobileIdentificationCredentialActionRequestModal(actionType: number, mobileIdNumber: number): void {
        const data: MobileIdentificationCredentialActionRequestModalContext = {
            actionType: actionType
        };

        this.modalService.open(MobileIdentificationCredentialActionRequestModalComponent, data, ModalSize.Small)
            .afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => {
                const mobileIdentificationCredentialActionRequest: MobileIdentificationCredentialActionRequest = {
                    mobileIdNumber: mobileIdNumber,
                    reasonTypeId: result
                };

                switch (actionType) {
                    case this.mobileIdentificationCredentialActionType.Suspend.id:
                        this.pauseMobileIdentification(mobileIdentificationCredentialActionRequest);
                        break;
                    case this.mobileIdentificationCredentialActionType.Resume.id:
                    default:
                        this.resumeMobileIdentification(mobileIdentificationCredentialActionRequest);
                        break;
                }
            });
    }

    private async resumeMobileIdentification(mobileIdentificationCredentialActionRequest: MobileIdentificationCredentialActionRequest) {
        try {
            await this.customerService.resumeMobileIdentification(mobileIdentificationCredentialActionRequest).pipe(first()).toPromise();
            this.currentMobileIds = await this.getMobileIds();
            await this.getMobileIdHistory();

            const message = 'The customer\'s Mobile Identification has been resumed.';
            const title = 'Resume Mobile Identification';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    private async getMobileIdHistory() {
        this.mobileIdHistory = (await this.customerService.getMobileIdentificationNumberHistory(this.viewService.customerNumberSubject.value).pipe(first()).toPromise());
        this.mobileIdHistory = this.mobileIdHistory ? this.mobileIdHistory.sort((a, b) => {
            var requestedDateDiff = new Date(b.requestedDate).getTime() - new Date(a.requestedDate).getTime();
            var updatedDateDiff = new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime();
            return requestedDateDiff !== 0 ? requestedDateDiff : updatedDateDiff;
        }) : null;
    }

    public exceptionLogClick() {
        this.router.navigate(['/exceptionlog'], { queryParams: { username: this.mvdNowStatus.exceptionLogUsername, source: 3 } });
    }

    public async linkToMvdNow() {
        const data = new MVDNowLinkCustomerContext(this.customerNumber);
        this.modalService.open(MVDNowLinkCustomerModalComponent, data, ModalSize.Medium).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(
                async (result) => {
                    if (result) {
                        this.mvdNowStatus = (await this.customerService.getMvdNowStatus(this.customerNumber).pipe(first()).toPromise());
                    }
                }
            );
    }

    private async getMobileIds(): Promise<MobileIdentificationNumber[]> {
        const data = await this.customerService.getMobileIdentificationNumbers(this.customerNumber).pipe(first()).toPromise();

        return data && data.result ? data.result : null;
    }

    public async forcedExtendedReviewMobileIdentification() {
        const message = 'This will require the customer to perform extended review when they attempt to provision a mobile identification credential in the future. This action cannot be undone.';
        const title = 'Require Extended Review for Mobile Identification Credentials';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Continue').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.confirmForcedExtendedReviewMobileIdentification(), () => { });
    }

    private async confirmForcedExtendedReviewMobileIdentification() {
        try {
            this.mobileIdAuthorization = await this.customerService.forcedExtendedReviewMobileIdentification(this.viewService.customerNumberSubject.value).pipe(first()).toPromise();

            const message = 'The customer is now required to complete extended review to provision mobile identification credentials.';
            const title = 'Require Extended Review for Mobile Identification Credentials';
            this.modalService.alert().title(title).body(message).okBtn('Close').open();
        } catch (err) {
            this.handleValidationResult(err);
        }
    }

    public getMobileIdentificationStatusTypes( data: MobileIdentificationCustomerAuthorization): string {
        if (!data.mobileIdentificationCustomerAuthorizationStatusTypeId) {
            return 'Mobile Id Unrestricted';
        }

        if (data.mobileIdentificationCustomerAuthorizationStatusTypeId === this.mobileIdentificationCustomerAuthorizationStatusType.ForcedExtendedReview.id) {
            return 'Extended Review Required';
        }

        return this.mobileIdentificationCustomerAuthorizationStatusType
            .FromId(data.mobileIdentificationCustomerAuthorizationStatusTypeId)
            .displayValue;
    }
}
